import { useQueryClient } from '@tanstack/react-query';
import { BaseSyntheticEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Checkbox, Flex, Text, useToast } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useEvaluation } from './context';
import { useFeedbackVisibility } from './hooks/use-feedback-visibility';

export const VisibilityToggle: FC = () => {
	const { t } = useTranslation('feedback');
	const evaluation = useEvaluation();
	const user = useCurrentUser();
	const queryClient = useQueryClient();

	const sharedWithUserId = evaluation.giverIs(user.id) ? user.id : user.manager_id;
	const isSharedWithManager = evaluation.sharedTo(sharedWithUserId);

	const [isChecked, setIsChecked] = useState(isSharedWithManager);
	useEffect(() => {
		setIsChecked(isSharedWithManager);
	}, [isSharedWithManager]);

	const toast = useToast();

	const { mutate: updateVisibility } = useFeedbackVisibility({
		onSuccess: () => {
			queryClient.invalidateQueries(['feedback', evaluation.id]);
			toast({
				title: t('visibility.update_success', {
					verb: isChecked ? 'shared with' : 'hidden from',
				}),
				status: 'success',
			});
		},
		onError: () => {
			setIsChecked(isSharedWithManager);
			toast({
				title: t('visibility.update_error'),
				status: 'error',
			});
		},
	});

	const handleChange = (event: BaseSyntheticEvent) => {
		const shareWithManager = event.target.checked;
		setIsChecked(shareWithManager);
		updateVisibility({
			evaluation_id: evaluation.id,
			sharedWithManager: shareWithManager,
		});
	};

	if (evaluation.receiverIs(user.id) && !evaluation.giverIs(user.manager_id)) {
		return (
			<Flex alignItems="center">
				<Box color="gray.700" fontSize="medium" px={1} py="2px">
					|
				</Box>
				<Checkbox
					name="toggle-visibility"
					py="1px"
					__css={{
						'& .chakra-checkbox__control': {
							backgroundColor: 'white',
							borderStyle: 'solid',
							borderColor: 'border.input',
							borderWidth: '1px',
						},
					}}
					isChecked={isChecked}
					onChange={handleChange}
				>
					<Text size="sm" color="gray.700" display="inline">
						{t('visibility.share_with_manager_brief')}
					</Text>
				</Checkbox>
			</Flex>
		);
	}

	return null;
};
